import { supabase } from '@/config/supabase'
import { useAuthStore } from '@/stores/auth.store'
import { usePortalStore } from '@/stores/portal.store'
import type { Veiculo } from '@/types/database'
import type { ListFilters, PaginatedResult } from './associado.service'

export type VeiculoComAssociado = Veiculo & { associado_nome?: string; ano_fabricacao?: number | null }

function mapVeiculo(v: any): VeiculoComAssociado {
    return {
        ...v,
        ano: v.ano ?? v.ano_fabricacao ?? null,
        associado_nome: v.associado?.nome ?? v.associado_nome ?? null,
    }
}

export const veiculoService = {
    async list(filters: ListFilters & { associado_id?: string; status?: string } = {}): Promise<PaginatedResult<VeiculoComAssociado>> {
        const { search, associado_id, status, page = 1, pageSize = 10 } = filters

        const authUser = useAuthStore.getState().user
        const portalCpf = usePortalStore.getState().cpf

        if (!authUser && portalCpf) {
            // Portal Mode: Fetch securely via RPC using CPF for unified view
            // @ts-ignore
            const { data, error } = await supabase.schema('veicular_system').rpc('get_portal_veiculos_por_cpf', { p_cpf: portalCpf })
            if (error) throw error
            const items = ((data as any[]) || []).map(mapVeiculo)
            return { data: items, total: items.length, page: 1, pageSize: 50, totalPages: 1 }
        }

        let query = supabase
            .schema('veicular_system')
            .from('veic_veiculos')
            .select('*, associado:veic_associados(nome)', { count: 'exact' })
            .is('deleted_at', null)
            .order('created_at', { ascending: false })

        if (search) {
            query = query.or(`placa.ilike.%${search}%,marca.ilike.%${search}%,modelo.ilike.%${search}%`)
        }
        if (associado_id) {
            query = query.eq('associado_id', associado_id)
        }
        if (status) {
            query = query.eq('status', status)
        }
        const from = (page - 1) * pageSize
        query = query.range(from, from + pageSize - 1)

        const { data, count, error } = await query
        if (error) throw error
        return {
            data: (data || []).map(mapVeiculo),
            total: count || 0, page, pageSize,
            totalPages: Math.ceil((count || 0) / pageSize),
        }
    },

    async getById(id: string): Promise<Veiculo | null> {
        const { data, error } = await supabase.schema('veicular_system').from('veic_veiculos').select('*, associado:veic_associados(nome)').eq('id', id).is('deleted_at', null).single()
        if (error) throw error
        return mapVeiculo(data)
    },

    async create(input: Partial<Veiculo>): Promise<Veiculo> {
        // @ts-ignore
        const { data, error } = await supabase.schema('veicular_system').from('veic_veiculos').insert(input as any).select().single()
        if (error) throw error
        return data as Veiculo
    },

    async update(id: string, input: Partial<Veiculo>): Promise<Veiculo> {
        // @ts-ignore
        const { data, error } = await supabase.schema('veicular_system').from('veic_veiculos').update(input as any).eq('id', id).select().single()
        if (error) throw error
        return data as Veiculo
    },

    async remove(id: string): Promise<void> {
        // @ts-ignore
        const { error } = await supabase.schema('veicular_system').from('veic_veiculos').update({ deleted_at: new Date().toISOString() } as any).eq('id', id)
        if (error) throw error
    },
}
